/**
 * TeamSelector.js
 * 
 * Account-Auswahl Screen für User mit mehreren Teams.
 * Wird IMMER zuerst angezeigt bevor die Module sichtbar werden.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { collection, query, where, getCountFromServer, Timestamp } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import { useTeam } from '../contexts/TeamContext';
import GenerativeOrbAnimation from '../Pages/GenerativeOrbAnimation';
import {
  formatGlanceCount,
  glanceWindows,
  sumTypedCounts,
} from './accountPickerStats';
import './TeamSelector.css';

// Maps the account's unlocked email plan (Users/{uid}/emailPlan) to a badge.
// Mirrors the labels used in the SalesGod header so the plan reads consistently
// across the app. Accounts without a plan fall back to the free Starter tier.
const PLAN_BADGE = {
  starter: { label: 'Starter', kind: 'starter' },
  pro: { label: 'Pro', kind: 'pro' },
  enterprise: { label: 'Max', kind: 'max' },
  // Stale cache keys from when the picker stored Users.emailPlan
  standard: { label: 'Starter', kind: 'starter' },
  premium: { label: 'Pro', kind: 'pro' },
  unlimited: { label: 'Max', kind: 'max' },
};

// Persistent (localStorage) caches so the picker doesn't re-bill Firestore /
// re-hit RTDB on every open or full reload. Keyed per current user so account
// switches never leak another user's data.
//
// Two TTLs, sized to how volatile each piece of data is:
//   - Glance stats (unread / sent 24h / sent 7d) drift slowly → 10 min.
//   - Photos & plan rarely change → 24 h.
const STATS_CACHE_TTL_MS = 10 * 60 * 1000;
const META_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const readCache = (key, ttl) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > ttl) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeCache = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ...value, ts: Date.now() }));
  } catch {
    /* localStorage full or unavailable — fail silently */
  }
};

// ── Glance stats cache ({ unread, sent7d, sent24h }) ──
// The payload carries a version: bumping it whenever the counting rule changes
// discards entries written by the old rule instead of letting them linger for a
// full TTL. Versioning the value rather than the key means the stale entry gets
// overwritten on the next fetch instead of orphaned in localStorage forever.
// v3: "Mails · 7d" switched from inbox received → world_sent, plus a 24h sent window.
const STATS_CACHE_VERSION = 3;

const statsCacheKey = (currentUserId, accountId) =>
  `bonusclub_stats_${currentUserId}_${accountId}`;

const readStatsFromCache = (currentUserId, accountId) => {
  const cached = readCache(statsCacheKey(currentUserId, accountId), STATS_CACHE_TTL_MS);
  if (!cached) return null;
  if (cached.v !== STATS_CACHE_VERSION) return null;
  if (
    typeof cached.unread !== 'number'
    || typeof cached.sent7d !== 'number'
    || typeof cached.sent24h !== 'number'
  ) return null;
  return { unread: cached.unread, sent7d: cached.sent7d, sent24h: cached.sent24h };
};

const writeStatsToCache = (currentUserId, accountId, stats) => {
  writeCache(statsCacheKey(currentUserId, accountId), { ...stats, v: STATS_CACHE_VERSION });
};

// ── Account meta cache ({ photo, plan }) — photo URL + email plan ──
// Split TTL: a real photo/plan stays cached for 24 h (they rarely change), but
// a cache entry that captured "no photo yet" only survives 5 min so freshly
// uploaded avatars actually appear in the account cards without the user
// having to wait a full day (or manually clear localStorage).
const META_EMPTY_TTL_MS = 5 * 60 * 1000;
const metaCacheKey = (currentUserId, accountId) =>
  `bonusclub_meta_${currentUserId}_${accountId}`;

const readMetaFromCache = (currentUserId, accountId) => {
  const cached = readCache(metaCacheKey(currentUserId, accountId), META_CACHE_TTL_MS);
  if (!cached) return null;
  const photo = cached.photo ?? null;
  const plan = cached.plan ?? null;
  const companyName = cached.companyName ?? null;
  // Negative-cache expiry: if any field is empty and the entry is older
  // than META_EMPTY_TTL_MS, treat it as stale so the caller refetches.
  if ((!photo || !plan || !companyName) && typeof cached.ts === 'number' && Date.now() - cached.ts > META_EMPTY_TTL_MS) {
    return null;
  }
  return { photo, plan, companyName };
};

const writeMetaToCache = (currentUserId, accountId, meta) => {
  writeCache(metaCacheKey(currentUserId, accountId), meta);
};

const TeamSelector = ({ onSelectTeam }) => {
  const { 
    availableTeams, 
    pendingInvitations,
    activeTeam,
    switchTeam,
    acceptInvitation,
    declineInvitation,
    leaveTeam,
    currentUserId,
    // Sub-World System
    multiInstanceEnabled,
    multiInstanceCap,
    isSubWorldSession,
    parentOwnerUid,
    enterSubWorld,
    returnToParentAccount,
    createSubWorld,
    renameSubWorld,
    archiveSubWorld,
    transferSubWorld,
  } = useTeam();
  
  const [processingId, setProcessingId] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(activeTeam?.teamId || null);
  const [accountPhotos, setAccountPhotos] = useState({});
  // Per-account unlocked email plan: { [teamId]: 'standard' | 'premium' | 'unlimited' }.
  const [accountPlans, setAccountPlans] = useState({});
  // Per-account company name (optional label next to the account title).
  const [accountCompanies, setAccountCompanies] = useState({});
  // Per-account glance stats: { [teamId]: { unread, sent7d, sent24h } }.
  const [accountStats, setAccountStats] = useState({});
  const [isExiting, setIsExiting] = useState(false);
  
  // Sub-World UI State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState('');
  const [manageSubWorld, setManageSubWorld] = useState(null); // { subWorldId, instanceName }
  const [manageMode, setManageMode] = useState(null);          // 'rename' | 'transfer' | 'archive' | null
  const [manageInput, setManageInput] = useState('');
  const [manageBusy, setManageBusy] = useState(false);
  const [manageError, setManageError] = useState('');
  // Welche Karte hat aktuell ihr 3-Dots-Menü offen (Sub-World ODER Team).
  const [openMenuId, setOpenMenuId] = useState(null);

  // Team, das der User gerade zu verlassen bestätigt. `null` = kein Modal offen.
  // Wir tracken die ganze Karte (nicht nur die ID), damit der Bestätigungs-
  // Dialog den Teamnamen und den Owner ohne zweiten Lookup anzeigen kann.
  const [leaveTeamTarget, setLeaveTeamTarget] = useState(null);
  const [leaveBusy, setLeaveBusy] = useState(false);
  const [leaveError, setLeaveError] = useState('');

  // Click-Outside: schließt das Dots-Menü, sobald irgendwo außerhalb geklickt wird.
  // Wir hören nur, wenn wirklich ein Menü offen ist, um den Listener-Footprint klein
  // zu halten. `closest('.ts-subworld-menu')` deckt sowohl Trigger als auch Dropdown ab.
  useEffect(() => {
    if (!openMenuId) return undefined;
    const onDocMouseDown = (e) => {
      if (!e.target.closest || !e.target.closest('.ts-subworld-menu')) {
        setOpenMenuId(null);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpenMenuId(null); };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenuId]);

  const allAccounts = availableTeams.map(team => ({
    ...team,
    isOwnAccount: team.teamId === currentUserId
  }));
  
  // Trennen: Hauptaccount, eigene Sub-Worlds (vom aktuellen User besessen),
  // fremde Team-Mitgliedschaften, Sub-Worlds anderer User wo ich Mitglied bin.
  const ownAccountEntry = allAccounts.find(a => a.isOwnAccount);
  const ownedSubWorldEntries = allAccounts.filter(a => !a.isOwnAccount && a.isSubWorld === true);
  const teamMembershipEntries = allAccounts.filter(a => !a.isOwnAccount && a.isSubWorld !== true);
  
  const activeSubWorldCount = ownedSubWorldEntries.length;
  const canCreateMore = multiInstanceEnabled && activeSubWorldCount < (multiInstanceCap || 10) && !isSubWorldSession;

  useEffect(() => {
    if (!currentUserId || allAccounts.length === 0) return;
    const db = getDatabase();
    let cancelled = false;

    // Seed from the persistent meta cache first so avatars + plan badges paint
    // instantly without any network round trip.
    const seedPhotos = {};
    const seedPlans = {};
    const seedCompanies = {};
    for (const account of allAccounts) {
      const cached = readMetaFromCache(currentUserId, account.teamId);
      if (cached) {
        if (cached.photo) seedPhotos[account.teamId] = cached.photo;
        if (cached.plan) seedPlans[account.teamId] = cached.plan;
        if (cached.companyName) seedCompanies[account.teamId] = cached.companyName;
      }
    }
    if (Object.keys(seedPhotos).length > 0) setAccountPhotos(prev => ({ ...prev, ...seedPhotos }));
    if (Object.keys(seedPlans).length > 0) setAccountPlans(prev => ({ ...prev, ...seedPlans }));
    if (Object.keys(seedCompanies).length > 0) setAccountCompanies(prev => ({ ...prev, ...seedCompanies }));

    // Only hit RTDB for accounts whose meta is missing or stale.
    const accountsToFetch = allAccounts.filter(
      account => readMetaFromCache(currentUserId, account.teamId) === null
    );
    if (accountsToFetch.length === 0) return;

    const loadPhotos = async () => {
      const results = await Promise.allSettled(
        accountsToFetch.map(async (account) => {
          const [photoSnap, membershipSnap, creditPlanSnap, companySnap] = await Promise.all([
            get(ref(db, `Users/${account.teamId}/userPhoto`)),
            get(ref(db, `Users/${account.teamId}/membershipPlan`)),
            get(ref(db, `World/${account.teamId}/creditPlan`)),
            get(ref(db, `Users/${account.teamId}/companyName`)),
          ]);
          const creditPlan = creditPlanSnap.exists() ? creditPlanSnap.val() : null;
          const membershipPlan = membershipSnap.exists() ? membershipSnap.val() : null;
          const planId = (creditPlan?.isActive && creditPlan.plan) || membershipPlan || null;
          return {
            teamId: account.teamId,
            photo: photoSnap.exists() ? photoSnap.val() : null,
            plan: planId,
            companyName: companySnap.exists() ? companySnap.val() : null,
          };
        })
      );

      if (cancelled) return;

      const photoMap = {};
      const planMap = {};
      const companyMap = {};
      for (const result of results) {
        if (result.status === 'fulfilled') {
          if (result.value.photo) photoMap[result.value.teamId] = result.value.photo;
          if (result.value.plan) planMap[result.value.teamId] = result.value.plan;
          if (result.value.companyName) companyMap[result.value.teamId] = result.value.companyName;
          writeMetaToCache(currentUserId, result.value.teamId, {
            photo: result.value.photo,
            plan: result.value.plan,
            companyName: result.value.companyName,
          });
        }
      }
      if (Object.keys(photoMap).length > 0) setAccountPhotos(prev => ({ ...prev, ...photoMap }));
      if (Object.keys(planMap).length > 0) setAccountPlans(prev => ({ ...prev, ...planMap }));
      if (Object.keys(companyMap).length > 0) setAccountCompanies(prev => ({ ...prev, ...companyMap }));
    };

    loadPhotos();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, availableTeams]);

  // Load unread + sent glance counts per account in parallel.
  // Uses Firestore COUNT aggregation (1 read each), heavy reliance on a
  // session cache so reopening the selector doesn't re-bill.
  useEffect(() => {
    if (!currentUserId || allAccounts.length === 0) return;
    let cancelled = false;

    // Skip accounts where we provably can't read world_inbox.
    // Team-Memberships need 'inbox' or 'all'; own account / sub-worlds where
    // the parent sits as master always have 'all'. This avoids both the
    // wasted read and the noisy permission-denied console errors.
    const canReadInbox = (account) => {
      if (account.isOwnAccount) return true;
      const f = account.features || [];
      return f.includes('all') || f.includes('inbox');
    };
    const eligibleAccounts = allAccounts.filter(canReadInbox);
    if (eligibleAccounts.length === 0) return;

    const cachedSeed = {};
    for (const account of eligibleAccounts) {
      const cached = readStatsFromCache(currentUserId, account.teamId);
      if (cached !== null) cachedSeed[account.teamId] = cached;
    }
    if (Object.keys(cachedSeed).length > 0) {
      setAccountStats(prev => ({ ...prev, ...cachedSeed }));
    }

    const accountsToFetch = eligibleAccounts.filter(
      account => readStatsFromCache(currentUserId, account.teamId) === null
    );
    if (accountsToFetch.length === 0) return;

    // Helper: count for a query, but never throw — a missing index or permission
    // issue should degrade to 0 rather than failing the whole account (which
    // would hide every badge/stat). `fallback` is returned on error.
    const safeCount = async (q, fallback = 0) => {
      try {
        const snap = await getCountFromServer(q);
        return snap.data().count;
      } catch {
        return fallback;
      }
    };

    const loadCounts = async () => {
      const { unreadSince, sentSince7d, sentSince24h } = glanceWindows();
      const results = await Promise.allSettled(
        accountsToFetch.map(async (account) => {
          const inboxRef = collection(firestore, 'world_inbox', account.teamId, 'emails');
          const sentRef = collection(firestore, 'world_sent', account.teamId, 'emails');

          // Unread over the recent (7d) window. Warmup mail and bounce notices
          // are both kept out of the badge: warmup is hidden from the inbox
          // entirely, and bounces are automated delivery reports that live on
          // their own inbox tab. Subtracting both keeps this badge to mail the
          // user actually has to look at. Inbox `timestamp` is epoch-ms.
          const unreadQuery = query(
            inboxRef,
            where('deleted', '==', false),
            where('isRead', '==', false),
            where('timestamp', '>=', unreadSince)
          );
          const warmupUnreadQuery = query(
            inboxRef,
            where('deleted', '==', false),
            where('isRead', '==', false),
            where('isWarmup', '==', true),
            where('timestamp', '>=', unreadSince)
          );
          const bounceUnreadQuery = query(
            inboxRef,
            where('deleted', '==', false),
            where('isRead', '==', false),
            where('isBounce', '==', true),
            where('timestamp', '>=', unreadSince)
          );

          // Sent counts: world_sent is the source of truth. Timestamp is either
          // a Firestore Timestamp or a legacy epoch-ms number — count both.
          const sent7dTsQuery = query(sentRef, where('timestamp', '>=', Timestamp.fromMillis(sentSince7d)));
          const sent7dNumQuery = query(sentRef, where('timestamp', '>=', sentSince7d));
          const sent24hTsQuery = query(sentRef, where('timestamp', '>=', Timestamp.fromMillis(sentSince24h)));
          const sent24hNumQuery = query(sentRef, where('timestamp', '>=', sentSince24h));

          // The unread query is the critical one — fail loudly here so a real
          // problem (e.g. permission denied) rejects the whole account instead
          // of silently showing 0. The subtractions and sent windows degrade.
          const unreadSnap = await getCountFromServer(unreadQuery);
          const [
            warmupUnread,
            bounceUnread,
            sent7dTs,
            sent7dNum,
            sent24hTs,
            sent24hNum,
          ] = await Promise.all([
            safeCount(warmupUnreadQuery),
            safeCount(bounceUnreadQuery),
            safeCount(sent7dTsQuery),
            safeCount(sent7dNumQuery),
            safeCount(sent24hTsQuery),
            safeCount(sent24hNumQuery),
          ]);

          const unread = Math.max(0, unreadSnap.data().count - warmupUnread - bounceUnread);
          const sent7d = sumTypedCounts(sent7dTs, sent7dNum);
          const sent24h = sumTypedCounts(sent24hTs, sent24hNum);
          return { teamId: account.teamId, stats: { unread, sent7d, sent24h } };
        })
      );

      if (cancelled) return;

      const next = {};
      for (const result of results) {
        if (result.status === 'fulfilled') {
          next[result.value.teamId] = result.value.stats;
          writeStatsToCache(currentUserId, result.value.teamId, result.value.stats);
        }
        // Failures (e.g. permission denied for accounts without inbox feature)
        // simply leave the stats hidden — no error UI for this glance feature.
      }
      if (Object.keys(next).length > 0) {
        setAccountStats(prev => ({ ...prev, ...next }));
      }
    };

    loadCounts();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, availableTeams]);

  // `route` is optional: a quick-action (e.g. "Inbox") can deep-link straight
  // into a specific module after the account is switched/entered. Without it we
  // fall back to the default destination (the dashboard) handled by onSelectTeam.
  const handleSelectTeam = useCallback(async (teamId, route) => {
    if (isExiting || processingId) return;
    setProcessingId(teamId);
    
    if (activeTeam?.teamId === teamId) {
      setIsExiting(true);
      setTimeout(() => {
        if (onSelectTeam) onSelectTeam(teamId, route);
      }, 350);
      return;
    }
    
    // Sub-World: per Custom-Token-Login einsteigen statt Team-Switch.
    const target = availableTeams.find(t => t.teamId === teamId);
    if (target?.isSubWorld === true) {
      const success = await enterSubWorld(teamId);
      if (success && onSelectTeam) {
        setIsExiting(true);
        setTimeout(() => onSelectTeam(teamId, route), 350);
      } else {
        setProcessingId(null);
      }
      return;
    }
    
    const success = await switchTeam(teamId);
    if (success && onSelectTeam) {
      setIsExiting(true);
      setTimeout(() => onSelectTeam(teamId, route), 350);
    } else {
      setProcessingId(null);
    }
  }, [isExiting, processingId, activeTeam, switchTeam, onSelectTeam, availableTeams, enterSubWorld]);
  
  const handleReturnToParent = useCallback(async () => {
    if (processingId) return;
    setProcessingId('__parent__');
    const success = await returnToParentAccount();
    if (!success) setProcessingId(null);
    // Nach erfolgreichem Token-Wechsel rerendert TeamSelector ohnehin via auth-Listener.
  }, [processingId, returnToParentAccount]);
  
  const handleCreateSubWorld = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const name = createName.trim();
    if (!name) {
      setCreateError('Please enter a name for this instance.');
      return;
    }
    setCreateBusy(true);
    setCreateError('');
    try {
      await createSubWorld(name);
      setShowCreateModal(false);
      setCreateName('');
    } catch (err) {
      setCreateError(err?.message || 'Failed to create instance.');
    } finally {
      setCreateBusy(false);
    }
  }, [createName, createSubWorld]);
  
  const openManage = (subWorld, mode) => {
    setManageSubWorld(subWorld);
    setManageMode(mode);
    setManageInput(mode === 'rename' ? (subWorld.teamName || '') : '');
    setManageError('');
  };
  
  const closeManage = () => {
    if (manageBusy) return;
    setManageSubWorld(null);
    setManageMode(null);
    setManageInput('');
    setManageError('');
  };
  
  const handleManageConfirm = async () => {
    if (!manageSubWorld) return;
    setManageBusy(true);
    setManageError('');
    try {
      if (manageMode === 'rename') {
        const name = manageInput.trim();
        if (!name) throw new Error('Please enter a new name.');
        await renameSubWorld(manageSubWorld.teamId, name);
      } else if (manageMode === 'transfer') {
        const email = manageInput.trim();
        if (!email) throw new Error('Please enter the new owner\'s email.');
        await transferSubWorld(manageSubWorld.teamId, email);
      } else if (manageMode === 'archive') {
        await archiveSubWorld(manageSubWorld.teamId);
      }
      closeManage();
    } catch (err) {
      setManageError(err?.message || 'Action failed.');
    } finally {
      setManageBusy(false);
    }
  };

  const handleAcceptInvitation = async (teamId) => {
    setProcessingId(teamId);
    await acceptInvitation(teamId);
    setProcessingId(null);
  };

  const handleDeclineInvitation = async (teamId) => {
    setProcessingId(teamId);
    await declineInvitation(teamId);
    setProcessingId(null);
  };

  // Bestätigungs-Modal öffnen. Wir setzen NIE direkt `leaveTeam` ab, ohne
  // vorher zu fragen – zu viele Daten (Inbox-Zugriff, Leads, laufende
  // Konversationen) hängen an einer Team-Mitgliedschaft, ein Fehlklick
  // wäre teuer wiederherzustellen.
  const openLeaveTeamModal = (team) => {
    setOpenMenuId(null);
    setLeaveTeamTarget(team);
    setLeaveError('');
  };

  const closeLeaveTeamModal = () => {
    if (leaveBusy) return;
    setLeaveTeamTarget(null);
    setLeaveError('');
  };

  const handleConfirmLeaveTeam = async () => {
    if (!leaveTeamTarget) return;
    setLeaveBusy(true);
    setLeaveError('');
    try {
      const success = await leaveTeam(leaveTeamTarget.teamId);
      if (!success) {
        throw new Error('Leaving the team failed. Please try again.');
      }
      // Erfolg: Modal schließen. availableTeams wurde vom Context bereits
      // aktualisiert, die Karte verschwindet ohne weiteres Zutun.
      setLeaveTeamTarget(null);
    } catch (err) {
      setLeaveError(err?.message || 'Leaving the team failed.');
    } finally {
      setLeaveBusy(false);
    }
  };

  return (
    <div className={`team-selector-container ${isExiting ? 'ts-page-exit' : 'ts-page-enter'}`}>
      <GenerativeOrbAnimation />
      
      <div className="team-selector-logo">
        <img src="/img/BonusClub_Logo.webp" alt="BonusClub" />
      </div>
      
      <div className="team-selector-content">
        <div className="team-selector-header">
          <h1><span className="title-highlight">Bonus</span>Club</h1>
          <p>Select an account to continue</p>
        </div>

        {pendingInvitations && pendingInvitations.length > 0 && (
          <div className="team-selector-section">
            <h3 className="section-title">
              <svg className="section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              Pending Invitations
            </h3>
            <div className="team-selector-list">
              {pendingInvitations.map((invitation) => (
                <div key={invitation.teamId} className="team-card invitation-card">
                  <span className="invitation-badge">NEW</span>
                  <div className="team-card-info">
                    <div className="team-card-avatar invitation">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </div>
                    <div className="team-card-details">
                      <h4>{invitation.teamName}</h4>
                      <p>Invited by {invitation.ownerName || invitation.ownerEmail}</p>
                      <span className="team-features">
                        {invitation.features?.includes('all') ? 'Full access' : invitation.features?.join(', ')}
                      </span>
                    </div>
                  </div>
                  <div className="invitation-actions">
                    <button
                      className="btn-accept"
                      onClick={() => handleAcceptInvitation(invitation.teamId)}
                      disabled={processingId !== null}
                    >
                      {processingId === invitation.teamId ? (
                        <span className="btn-spinner"></span>
                      ) : 'Accept'}
                    </button>
                    <button
                      className="btn-decline"
                      onClick={() => handleDeclineInvitation(invitation.teamId)}
                      disabled={processingId !== null}
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sub-World Session Banner: zurück zum Hauptaccount */}
        {isSubWorldSession && (
          <div className="ts-subworld-banner">
            <div className="ts-subworld-banner-info">
              <span className="ts-subworld-banner-label">Sub-Instance Session</span>
              <p>You are signed in to a sub-instance. Return to your main account to manage other instances.</p>
            </div>
            <button
              className="ts-subworld-banner-btn"
              onClick={handleReturnToParent}
              disabled={processingId !== null}
            >
              {processingId === '__parent__' ? (
                <span className="btn-spinner"></span>
              ) : 'Switch to Main Account'}
            </button>
          </div>
        )}

        {/* Hauptaccount + Team-Mitgliedschaften */}
        {(ownAccountEntry || teamMembershipEntries.length > 0) && (
          <div className="team-selector-accounts">
            {ownAccountEntry && renderAccountCard(ownAccountEntry, accountPhotos, processingId, handleSelectTeam, false, accountStats, accountPlans, null, accountCompanies)}
            {teamMembershipEntries.map(account =>
              renderAccountCard(
                account,
                accountPhotos,
                processingId,
                handleSelectTeam,
                false,
                accountStats,
                accountPlans,
                // 3-Dots-Menü mit einziger Aktion "Leave Team" – gleiches
                // UI-Pattern wie bei den Sub-Worlds, damit der User es nur
                // einmal lernen muss.
                {
                  isOpen: openMenuId === account.teamId,
                  onToggle: (e) => {
                    e.stopPropagation();
                    setOpenMenuId(prev => prev === account.teamId ? null : account.teamId);
                  },
                  onAction: (mode) => {
                    setOpenMenuId(null);
                    if (mode === 'leave') openLeaveTeamModal(account);
                  },
                  // Signalisiert renderAccountCard, dass es das reduzierte
                  // Team-Menü (nur "Leave") statt des vollen Sub-World-Menüs
                  // (Rename/Transfer/Archive) rendern soll.
                  variant: 'team',
                },
                accountCompanies,
              )
            )}
          </div>
        )}

        {/* Sub-World Section (nur sichtbar, wenn Feature freigeschaltet ODER bestehende Sub-Worlds vorhanden) */}
        {!isSubWorldSession && (multiInstanceEnabled || ownedSubWorldEntries.length > 0) && (
          <div className="team-selector-section ts-subworlds-section">
            <h3 className="ts-instances-section-title">
              <svg className="section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1.5"/>
                <rect x="14" y="3" width="7" height="7" rx="1.5"/>
                <rect x="3" y="14" width="7" height="7" rx="1.5"/>
                <rect x="14" y="14" width="7" height="7" rx="1.5"/>
              </svg>
              My Instances
              {multiInstanceCap > 0 && (
                <span className="ts-subworld-count">
                  {ownedSubWorldEntries.length}/{multiInstanceCap}
                </span>
              )}
            </h3>
            <div className="team-selector-accounts">
              {ownedSubWorldEntries.map(account => renderAccountCard(
                account,
                accountPhotos,
                processingId,
                handleSelectTeam,
                true,
                accountStats,
                accountPlans,
                {
                  isOpen: openMenuId === account.teamId,
                  onToggle: (e) => {
                    e.stopPropagation();
                    setOpenMenuId(prev => prev === account.teamId ? null : account.teamId);
                  },
                  onAction: (mode) => {
                    setOpenMenuId(null);
                    openManage(account, mode);
                  },
                },
                accountCompanies,
              ))}
              
              {canCreateMore && (
                <button
                  type="button"
                  className="ts-subworld-create-card"
                  onClick={() => { setCreateName(''); setCreateError(''); setShowCreateModal(true); }}
                >
                  <div className="ts-subworld-create-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </div>
                  <div className="ts-subworld-create-text">
                    <h4>Add new instance</h4>
                    <p>Create a fully isolated workspace with separate data, emails and team members.</p>
                  </div>
                </button>
              )}
              
              {multiInstanceEnabled && !canCreateMore && ownedSubWorldEntries.length >= multiInstanceCap && (
                <div className="ts-subworld-cap-note">
                  Instance limit reached ({multiInstanceCap}). Archive an existing instance or contact support to raise the cap.
                </div>
              )}
            </div>
          </div>
        )}

        {allAccounts.length === 0 && (!pendingInvitations || pendingInvitations.length === 0) && (
          <div className="team-selector-empty">
            <p>No accounts available. Please wait for an invitation or contact support.</p>
          </div>
        )}
      </div>
      
      {/* Create Sub-World Modal */}
      {showCreateModal && (
        <div className="ts-modal-overlay" onClick={() => !createBusy && setShowCreateModal(false)}>
          <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ts-modal-header">
              <h3>New Instance</h3>
              <button className="ts-modal-close" onClick={() => !createBusy && setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateSubWorld}>
              <div className="ts-modal-body">
                <p className="ts-modal-description">
                  This creates a fully isolated workspace. The new instance has its own emails, contacts, settings and team members. You stay the owner.
                </p>
                <label className="ts-modal-label">Instance name</label>
                <input
                  type="text"
                  className="ts-modal-input"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="e.g. Brand X, Client Y, Test Lab"
                  maxLength={60}
                  autoFocus
                  disabled={createBusy}
                />
                {createError && <p className="ts-modal-error">{createError}</p>}
              </div>
              <div className="ts-modal-footer">
                <button
                  type="button"
                  className="ts-modal-btn ts-modal-btn-secondary"
                  onClick={() => !createBusy && setShowCreateModal(false)}
                  disabled={createBusy}
                >Cancel</button>
                <button
                  type="submit"
                  className="ts-modal-btn ts-modal-btn-primary"
                  disabled={createBusy || !createName.trim()}
                >
                  {createBusy ? 'Creating…' : 'Create Instance'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Manage Sub-World Modal */}
      {manageSubWorld && manageMode && (
        <div className="ts-modal-overlay" onClick={closeManage}>
          <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ts-modal-header">
              <h3>
                {manageMode === 'rename' && 'Rename Instance'}
                {manageMode === 'transfer' && 'Transfer Ownership'}
                {manageMode === 'archive' && 'Archive Instance'}
              </h3>
              <button className="ts-modal-close" onClick={closeManage}>×</button>
            </div>
            <div className="ts-modal-body">
              <p className="ts-modal-description">
                <strong>{manageSubWorld.teamName}</strong>
              </p>
              {manageMode === 'rename' && (
                <>
                  <label className="ts-modal-label">New name</label>
                  <input
                    type="text"
                    className="ts-modal-input"
                    value={manageInput}
                    onChange={(e) => setManageInput(e.target.value)}
                    maxLength={60}
                    autoFocus
                    disabled={manageBusy}
                  />
                </>
              )}
              {manageMode === 'transfer' && (
                <>
                  <p className="ts-modal-description">
                    Transfer this instance to another BonusClub user. The new owner will manage all data, emails and team members. You will lose access.
                  </p>
                  <label className="ts-modal-label">New owner email</label>
                  <input
                    type="email"
                    className="ts-modal-input"
                    value={manageInput}
                    onChange={(e) => setManageInput(e.target.value)}
                    placeholder="user@example.com"
                    autoFocus
                    disabled={manageBusy}
                  />
                </>
              )}
              {manageMode === 'archive' && (
                <p className="ts-modal-description">
                  Archiving deactivates this instance. Login is blocked and the instance disappears from your list. Data is preserved and can be restored on request.
                </p>
              )}
              {manageError && <p className="ts-modal-error">{manageError}</p>}
            </div>
            <div className="ts-modal-footer">
              <button
                type="button"
                className="ts-modal-btn ts-modal-btn-secondary"
                onClick={closeManage}
                disabled={manageBusy}
              >Cancel</button>
              <button
                type="button"
                className={`ts-modal-btn ${manageMode === 'archive' ? 'ts-modal-btn-danger' : 'ts-modal-btn-primary'}`}
                onClick={handleManageConfirm}
                disabled={manageBusy || (manageMode !== 'archive' && !manageInput.trim())}
              >
                {manageBusy
                  ? 'Working…'
                  : manageMode === 'rename'
                    ? 'Save'
                    : manageMode === 'transfer'
                      ? 'Transfer'
                      : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leave Team Confirmation Modal.
          Bewusst als eigener, schmaler Dialog – nicht als Reuse des Manage-
          Modals. Das Manage-Modal ist an Sub-World-Ownership gebunden; Leave
          ist die entgegengesetzte Operation (Rechte VERLIEREN, nicht managen)
          und braucht eine klar "danger"-gefärbte, kurze Bestätigung. */}
      {leaveTeamTarget && (
        <div className="ts-modal-overlay" onClick={closeLeaveTeamModal}>
          <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ts-modal-header">
              <h3>Leave Team</h3>
              <button className="ts-modal-close" onClick={closeLeaveTeamModal}>×</button>
            </div>
            <div className="ts-modal-body">
              <p className="ts-modal-description">
                Are you sure you want to leave <strong>{leaveTeamTarget.teamName}</strong>?
              </p>
              <p className="ts-modal-description">
                You will immediately lose access to this account's leads, inbox, offers and settings.
                {leaveTeamTarget.ownerEmail && (
                  <> Only <strong>{leaveTeamTarget.ownerName || leaveTeamTarget.ownerEmail}</strong> can invite you back.</>
                )}
              </p>
              {leaveError && <p className="ts-modal-error">{leaveError}</p>}
            </div>
            <div className="ts-modal-footer">
              <button
                type="button"
                className="ts-modal-btn ts-modal-btn-secondary"
                onClick={closeLeaveTeamModal}
                disabled={leaveBusy}
              >Cancel</button>
              <button
                type="button"
                className="ts-modal-btn ts-modal-btn-danger"
                onClick={handleConfirmLeaveTeam}
                disabled={leaveBusy}
              >
                {leaveBusy ? 'Leaving…' : 'Leave Team'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Hilfsfunktion: rendert eine Account-Karte (eigener Account, Sub-World, oder fremdes Team).
//
// Optionales `menu`-Objekt fügt ein 3-Dots-Overflow-Menü in der oberen rechten Ecke
// der Karte ein. Wird ausschließlich für eigene Sub-World-Instanzen verwendet (Rename /
// Transfer / Archive). Klicks auf Trigger und Dropdown-Items werden bewusst gestoppt,
// damit nicht zugleich der Karte-Click (= Sub-World betreten) feuert.
function renderAccountCard(account, accountPhotos, processingId, handleSelectTeam, isSubWorldVariant = false, accountStats = {}, accountPlans = {}, menu = null, accountCompanies = {}) {
  const photo = accountPhotos[account.teamId];
  // Show the unlocked plan (Starter / Pro / Max) instead of the account type.
  const planInfo = PLAN_BADGE[accountPlans[account.teamId]] || null;
  const badgeKind = planInfo?.kind;
  const badgeLabel = planInfo?.label;
  const companyName = accountCompanies[account.teamId] || null;
  const stats = accountStats[account.teamId];
  const unread = stats?.unread;
  const sent7d = stats?.sent7d;
  const sent24h = stats?.sent24h;
  const hasStats = stats != null;
  const hasUnread = typeof unread === 'number' && unread > 0;
  const hasRecentSent = typeof sent24h === 'number' && sent24h > 0;
  const fullAccess = account.features?.includes('all');
  const accessLabel = fullAccess
    ? 'Full access'
    : (account.features?.length ? `${account.features.length} module${account.features.length === 1 ? '' : 's'}` : 'Limited');
  return (
    <div
      key={account.teamId}
      className={`account-nav-container ${processingId === account.teamId ? 'loading' : ''} ${isSubWorldVariant ? 'is-subworld' : ''}`}
      onClick={() => handleSelectTeam(account.teamId)}
    >
      <div className={`account-nav-icon ${account.isOwnAccount ? 'own' : (account.isSubWorld ? 'subworld' : 'team')} ${photo ? 'has-photo' : ''}`}>
        {photo ? (
          <img
            src={photo}
            alt={account.teamName}
            className="account-nav-photo"
            // Elegant fade-in: cached images are already `complete` on mount, so
            // mark them loaded immediately via the ref; freshly fetched images
            // fade in on their load event. Avoids the hectic pop/flicker.
            ref={(el) => { if (el && el.complete) el.classList.add('is-loaded'); }}
            onLoad={(e) => e.currentTarget.classList.add('is-loaded')}
          />
        ) : account.isSubWorld ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" rx="1.5"/>
            <rect x="14" y="3" width="7" height="7" rx="1.5"/>
            <rect x="3" y="14" width="7" height="7" rx="1.5"/>
            <rect x="14" y="14" width="7" height="7" rx="1.5"/>
          </svg>
        ) : account.isOwnAccount ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        )}
        {hasUnread && (
          <span
            className="account-nav-unread-badge"
            title={`${unread} unread email${unread === 1 ? '' : 's'} in the last 7 days`}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </div>
      <div className="account-nav-content">
        <div className="account-nav-header">
          <h3 className="account-nav-title">
            {account.teamName}
            {companyName && <span className="account-nav-company">· {companyName}</span>}
          </h3>
          {planInfo && <span className={`account-nav-badge ${badgeKind}`}>{badgeLabel}</span>}
        </div>
        <p className="account-nav-description">
          {account.isOwnAccount
            ? (account.isSubWorldSession ? 'You are signed in to this instance' : 'Your personal account')
            : account.isSubWorld
              ? 'Sign in to operate this isolated workspace'
              : `Managed by ${account.ownerName || account.ownerEmail}`}
        </p>
        <div className="account-nav-stats">
          <div className={`account-nav-stat ${hasUnread ? 'is-active' : ''}`}>
            <span className="account-nav-stat-value">{hasStats ? formatGlanceCount(unread) : '—'}</span>
            <span className="account-nav-stat-label">Unread</span>
            <span className="account-nav-stat-hint">Last 7 days</span>
          </div>
          <div
            className={`account-nav-stat ${hasRecentSent ? 'is-active' : ''}`}
            title={hasStats ? `${sent24h} email${sent24h === 1 ? '' : 's'} sent in the last 24 hours` : undefined}
          >
            <span className="account-nav-stat-value">{hasStats ? formatGlanceCount(sent24h) : '—'}</span>
            <span className="account-nav-stat-label">Sent · 24h</span>
            <span className="account-nav-stat-hint">Last 24 hours</span>
          </div>
          <div
            className="account-nav-stat"
            title={hasStats ? `${sent7d} email${sent7d === 1 ? '' : 's'} sent in the last 7 days` : undefined}
          >
            <span className="account-nav-stat-value">{hasStats ? formatGlanceCount(sent7d) : '—'}</span>
            <span className="account-nav-stat-label">Sent · 7d</span>
            <span className="account-nav-stat-hint">Last 7 days</span>
          </div>
          <div className="account-nav-stat account-nav-stat--access">
            <span className="account-nav-stat-value">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </span>
            <span className="account-nav-stat-label">Access</span>
            <span className="account-nav-stat-hint">{accessLabel}</span>
          </div>
        </div>
      </div>
      <span className="account-nav-arrow">
        {processingId === account.teamId ? (
          <span className="btn-spinner dark"></span>
        ) : <img src="/img/down.svg" alt="→" className="account-nav-arrow-icon" />}
      </span>

      {menu && (
        <div className="ts-subworld-menu" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className={`ts-subworld-menu-trigger ${menu.isOpen ? 'is-open' : ''}`}
            onClick={menu.onToggle}
            aria-label={menu.variant === 'team' ? 'Team options' : 'Instance options'}
            aria-haspopup="menu"
            aria-expanded={menu.isOpen ? 'true' : 'false'}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
              <circle cx="5" cy="12" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="19" cy="12" r="2" />
            </svg>
          </button>
          {menu.isOpen && (
            <div className="ts-subworld-menu-dropdown" role="menu">
              {menu.variant === 'team' ? (
                // Team-Mitgliedschaften bekommen NUR "Leave Team". Rename/
                // Transfer/Archive machen für fremde Teams keinen Sinn –
                // die gehören dem Owner, nicht dem Member.
                <button
                  type="button"
                  role="menuitem"
                  className="ts-subworld-menu-item ts-subworld-menu-item-danger"
                  onClick={() => menu.onAction('leave')}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Leave Team
                </button>
              ) : (
                // Sub-World-Variante: volles Verwaltungsmenü für eigene Instanzen.
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="ts-subworld-menu-item"
                    onClick={() => menu.onAction('rename')}
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                    Rename
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="ts-subworld-menu-item"
                    onClick={() => menu.onAction('transfer')}
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="16 3 21 3 21 8" />
                      <line x1="14" y1="10" x2="21" y2="3" />
                      <polyline points="8 21 3 21 3 16" />
                      <line x1="10" y1="14" x2="3" y2="21" />
                    </svg>
                    Transfer
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="ts-subworld-menu-item ts-subworld-menu-item-danger"
                    onClick={() => menu.onAction('archive')}
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="21 8 21 21 3 21 3 8" />
                      <rect x="1" y="3" width="22" height="5" />
                      <line x1="10" y1="12" x2="14" y2="12" />
                    </svg>
                    Archive
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TeamSelector;
